import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { GitMerge } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import AdminModal from '../../components/AdminModal';
import SearchSelect, { SearchOption } from '../../components/SearchSelect';
import { searchPersons } from '../../lib/pickers';

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ??
  error?.response?.data?.message ??
  error?.response?.data?.errors?.[0]?.msg ??
  fallback;

/**
 * Merge this person with any other person record — the path for duplicates the
 * Duplicates review cannot pair because their names differ (Bob vs Robert
 * Marshall: one from the legislator load, one from the candidate import).
 * Same endpoint and rules as that review: a dry-run impact preview first, then
 * an explicit merge that moves every linked record to the kept person and
 * deletes the other.
 */
const MergePersonModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  person: { id: string; name: string };
}> = ({ isOpen, onClose, person }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [other, setOther] = useState<SearchOption | null>(null);
  const [keep, setKeep] = useState<'this' | 'other'>('this');
  const [impact, setImpact] = useState<string | null>(null);

  const reset = () => {
    setOther(null);
    setKeep('this');
    setImpact(null);
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  const kept = keep === 'this' ? { id: person.id, name: person.name } : { id: other?.id ?? '', name: other?.label ?? '' };
  const merged = keep === 'this' ? { id: other?.id ?? '', name: other?.label ?? '' } : { id: person.id, name: person.name };

  // The current person never appears as a merge partner.
  const fetchOthers = useCallback(
    async (term: string) => (await searchPersons(term)).filter((option) => option.id !== person.id),
    [person.id]
  );

  const previewMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/admin/candidates/merge-persons', {
        source_person_id: merged.id,
        target_person_id: kept.id,
        dry_run: true,
      });
      return (response.data.data?.impact_before ?? []) as Array<{ table_name: string; count: number }>;
    },
    onSuccess: (rows) => {
      const touched = rows
        .filter((row) => row.count > 0)
        .map((row) => `${row.table_name} (${row.count})`)
        .join(', ');
      setImpact(touched || 'no linked records');
    },
    onError: (error: any) => toast.error(apiError(error, 'Merge preview failed')),
  });

  const mergeMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/candidates/merge-persons', {
        source_person_id: merged.id,
        target_person_id: kept.id,
        dry_run: false,
        reason: 'People tool: merge with another person',
      }),
    onSuccess: () => {
      toast.success(`Merged into ${kept.name}`);
      queryClient.invalidateQueries({ queryKey: ['admin-people'] });
      queryClient.invalidateQueries({ queryKey: ['admin-list'] });
      const keptId = kept.id;
      handleClose();
      // This page's record was merged away: go to the one that remains.
      if (keptId !== person.id) navigate(`/admin-next/people/${keptId}`);
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to merge person records')),
  });

  return (
    <AdminModal
      isOpen={isOpen}
      title="Merge with another person"
      onClose={handleClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          {impact === null ? (
            <Button
              variant="primary"
              size="sm"
              icon={GitMerge}
              disabled={!other}
              loading={previewMutation.isLoading}
              onClick={() => previewMutation.mutate()}
            >
              Preview merge
            </Button>
          ) : (
            <Button
              variant="danger"
              size="sm"
              icon={GitMerge}
              loading={mergeMutation.isLoading}
              onClick={() => mergeMutation.mutate()}
            >
              Merge
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          For one person recorded twice under different names — a nickname, a middle name, a
          typo. Every linked record (candidacies, legislator roster, roles, contacts, addresses)
          moves to the person you keep, and the other record is deleted.
        </p>

        <div>
          <span className="mb-1 block text-xs font-semibold text-text-secondary dark:text-text-dark-secondary">
            Other person *
          </span>
          <SearchSelect
            placeholder="Search people…"
            fetchOptions={fetchOthers}
            selected={other}
            onSelect={(option) => {
              setOther(option);
              setImpact(null);
            }}
            onClear={() => {
              setOther(null);
              setImpact(null);
            }}
            ariaLabel="Search the person to merge with"
          />
        </div>

        {other && (
          <fieldset>
            <legend className="mb-1 text-xs font-semibold text-text-secondary dark:text-text-dark-secondary">
              Keep which record?
            </legend>
            {([
              ['this', person.name, 'this page'],
              ['other', other.label, other.sublabel],
            ] as const).map(([value, label, hint]) => (
              <label key={value} className="flex cursor-pointer items-center gap-2 py-1 text-sm">
                <input
                  type="radio"
                  name="merge-keep"
                  checked={keep === value}
                  onChange={() => {
                    setKeep(value);
                    setImpact(null);
                  }}
                />
                <span className="font-medium text-text-primary dark:text-text-dark-primary">{label}</span>
                {hint && <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{hint}</span>}
              </label>
            ))}
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              The kept record&apos;s name and details win; blanks are filled from the other. Usually
              keep the one with the legislator roster rows.
            </p>
          </fieldset>
        )}

        {impact !== null && (
          <div className="rounded-lg border border-error-300 bg-error-50 p-3 dark:border-error-700 dark:bg-error-900 dark:bg-opacity-20">
            <p className="text-sm text-text-primary dark:text-text-dark-primary">
              Merge &quot;{merged.name}&quot; into &quot;{kept.name}&quot;? Affected: {impact}.
              &quot;{merged.name}&quot; is deleted. This cannot be undone.
            </p>
          </div>
        )}
      </div>
    </AdminModal>
  );
};

export default MergePersonModal;
